import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/pages/Blog";
import { ShareLinks } from "@/components/ShareLinks";
import { FollowLinks } from "@/components/FollowLinks";

export const DEFAULT_BIO = "Somnio.Co began with a simple yet powerful idea: to re-imagine diamonds for the modern world through innovation, integrity and impact. Every piece is crafted in our atelier with lab-grown diamonds and recycled metals, designed to celebrate beauty, honour responsible innovation and hold meaning for generations to come.";

export function AuthorBox({ p, large = false }: { p: any; large?: boolean }) {
  return (
    <section className={large ? "bg-[var(--c-muted)] p-8 md:p-10" : ""} data-testid={large ? "post-author-box" : "post-author-side"}>
      <p className="font-display uppercase tracking-[0.12em] text-lg">Authors &amp; Experts</p>
      <div className={`flex items-center gap-4 ${large ? "mt-6" : "mt-4"}`}>
        <img src="/shield-somnio.png" alt="" className={`${large ? "h-14 w-14" : "h-12 w-12"} object-contain`} />
        <Link to="/pages/about" className="underline underline-offset-4 text-sm">{p.author || "Somnio.Co"}</Link>
      </div>
      {large && <p className="mt-5 text-sm leading-7 text-neutral-700">{p.author_bio || DEFAULT_BIO}</p>}
    </section>
  );
}

export function RelatedPosts({ items }: { items: any[] }) {
  if (!items?.length) return null;
  return (
    <section data-testid="post-related">
      <p className="text-lg"><em className="font-display">Don't Miss!</em> <span className="font-display uppercase tracking-[0.08em]">Related Posts</span></p>
      <div className="mt-6 space-y-6">
        {items.map((r) => (
          <Link key={r.slug} to={`/blog/${r.slug}`} className="group grid grid-cols-[104px_1fr] gap-4" data-testid={`related-${r.slug}`}>
            <div className="relative overflow-hidden bg-neutral-100 aspect-square">
              <img src={r.image} alt="" loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
              {r.category && <span className="absolute top-2 left-2 bg-neutral-900/80 text-white text-[10px] px-1.5 py-0.5">{r.category}</span>}
            </div>
            <div><p className="font-display text-base leading-snug line-clamp-3 group-hover:text-[var(--c-primary)] transition-colors">{r.title}</p>
              <p className="mt-2 text-[11px] text-neutral-500">{fmtDate(r.date)} | By {r.author || "Somnio.Co"}</p></div>
          </Link>
        ))}
      </div>
    </section>
  );
}

export function SideNewsletter() {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    try { await api.post("/newsletter", { email }); setDone(true); toast("Thank you for subscribing"); } catch (x) { toast.error(errMsg(x)); }
  };
  if (done) return <p className="text-sm text-neutral-600" data-testid="post-newsletter-done">You're on the list — thank you.</p>;
  return (
    <form onSubmit={submit} className="flex gap-2" data-testid="post-newsletter">
      <label className="sr-only" htmlFor="post-nl-email">Email address</label>
      <input id="post-nl-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email address" className="flex-1 min-w-0 border border-neutral-300 rounded-full px-4 py-2.5 text-xs bg-white" data-testid="post-newsletter-email" />
      <button className="rounded-full bg-neutral-900 text-white px-6 text-[11px] tracking-[0.18em] uppercase hover:bg-[var(--c-primary)] transition-colors" data-testid="post-newsletter-submit">Sign up</button>
    </form>
  );
}


export function PostFooter({ p }: { p: any }) {
  return (
    <div className="mt-16 space-y-10" data-testid="post-footer">
      <nav className="flex justify-between gap-6 text-sm text-neutral-600" aria-label="More posts">
        {p.prev ? <Link to={`/blog/${p.prev.slug}`} className="inline-flex items-center gap-1 hover:text-neutral-900" data-testid="post-prev"><ChevronLeft size={16} />Previous Post</Link> : <span />}
        {p.next ? <Link to={`/blog/${p.next.slug}`} className="inline-flex items-center gap-1 hover:text-neutral-900" data-testid="post-next">Next Post<ChevronRight size={16} /></Link> : <span />}
      </nav>
      <ShareLinks path={`/blog/${p.slug}`} title={p.title} image={p.image} track={`/blog/${p.slug}/share`} testPrefix="post-share" className="flex justify-center gap-12" />
      {p.category && <p className="text-center text-sm tracking-[0.12em] uppercase" data-testid="post-tags">Tags: <Link to={`/blog?category=${encodeURIComponent(p.category)}`} className="normal-case tracking-normal hover:underline">{p.category}</Link></p>}
      <FollowLinks />
      <AuthorBox p={p} large />
    </div>
  );
}
